import { AdminPageHeader } from '@/components/patterns/admin-page'
import SearchInput from '@/components/form/SearchInput'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import ImageTileMetadata from '@/components/media/ImageTileMetadata'
import { StockImageLicensingNotice } from '@/components/stock-images/StockImageLicensingNotice'
import { Badge, BadgeAction } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { useMerchandisingTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.query'
import {
  flattenMerchandisingTagGroups,
  splitMerchandisingTagIds,
} from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import {
  STOCK_IMAGES_PAGE_SIZE,
  stockImagesQueries,
  useStockImageTags,
  useStockImages,
} from '@/lib/services/stock-images/stock-images.query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import { CalendarHeart, ChevronDown, ImageIcon, Images, ListFilter, Loader2, X } from 'lucide-react'
import { useMemo, useState } from 'react'

export const Route = createFileRoute('/_authenticated/$orgId/stock-images/')({
  component: StockImagesIndexPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'stock-images') || !context.ability.can('access', 'global')) {
      throw notFound()
    }

    await context.queryClient.ensureInfiniteQueryData(
      stockImagesQueries.index({ organizationId: params.orgId, pageSize: STOCK_IMAGES_PAGE_SIZE })
    )

    return { isReadOnly: !context.ability.can('edit', 'stock-images') }
  },
})

function getAssetMedia(asset) {
  return asset?.latest_media || asset?.original_media || {}
}

function getAssetTitle(asset) {
  return asset?.title || getAssetMedia(asset).name || `Stock image #${asset.id}`
}

export function StockImagesIndexPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState([])
  const [selectedMerchandisingTagIds, setSelectedMerchandisingTagIds] = useState([])
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const visibleSearchTerm = searchTerm.trim()
  const normalizedSearchTerm = visibleSearchTerm ? debouncedSearchTerm.trim() : ''
  const selectedTagIdSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds])
  const selectedMerchandisingTagIdSet = useMemo(
    () => new Set(selectedMerchandisingTagIds),
    [selectedMerchandisingTagIds]
  )
  const { data: merchandisingTagGroups = [], isLoading: isLoadingOccasions } =
    useMerchandisingTagGroups()
  const merchandisingTagOptions = flattenMerchandisingTagGroups(merchandisingTagGroups)
  const { occasionIds: selectedOccasionIds, seasonIds: selectedSeasonIds } =
    splitMerchandisingTagIds(selectedMerchandisingTagIds, merchandisingTagOptions)
  const stockImageFilters = useMemo(() => {
    const filters = {
      ...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
      ...(selectedTagIds.length > 0 ? { tag_ids: selectedTagIds } : {}),
      ...(selectedOccasionIds.length > 0 ? { occasion_ids: selectedOccasionIds } : {}),
      ...(selectedSeasonIds.length > 0 ? { season_ids: selectedSeasonIds } : {}),
    }

    return Object.keys(filters).length > 0 ? filters : undefined
  }, [normalizedSearchTerm, selectedOccasionIds, selectedSeasonIds, selectedTagIds])
  const {
    data: stockImagesResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useStockImages({ filters: stockImageFilters })
  const { data: tagsResponse, isLoading: isLoadingTags } = useStockImageTags()
  const stockImagePages = stockImagesResponse?.pages
  const stockImages = useMemo(
    () => stockImagePages?.flatMap((page) => page?.data ?? []) ?? [],
    [stockImagePages]
  )
  const stockTags = useMemo(() => tagsResponse?.data ?? [], [tagsResponse?.data])
  const selectedTags = useMemo(
    () => stockTags.filter((tag) => selectedTagIdSet.has(tag.id)),
    [selectedTagIdSet, stockTags]
  )
  const hasActiveFilters =
    visibleSearchTerm !== '' || selectedTagIds.length > 0 || selectedMerchandisingTagIds.length > 0
  const path = usePathHelper()

  const toggleTagFilter = (tagId, checked) => {
    setSelectedTagIds((current) => {
      if (checked) {
        return current.includes(tagId) ? current : [...current, tagId]
      }

      return current.filter((selectedTagId) => selectedTagId !== tagId)
    })
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedTagIds([])
    setSelectedMerchandisingTagIds([])
  }

  const toggleOccasionFilter = (occasionId, checked) => {
    setSelectedMerchandisingTagIds((current) => {
      if (checked) {
        return current.includes(occasionId) ? current : [...current, occasionId]
      }

      return current.filter((selectedOccasionId) => selectedOccasionId !== occasionId)
    })
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Images}
        title="Stock Images"
        description="Curate the image library florists can insert from the Photo Manager."
        actions={
          <Can I="create" a="stock-images">
            <CreateActionButton asChild>
              <Link {...path.stockImages.create().link}>Create Stock Image</Link>
            </CreateActionButton>
          </Can>
        }
      />

      <StockImageLicensingNotice />

      <div className="@container/stock-image-filters rounded-md border border-border bg-card p-3">
        <div className="flex flex-col gap-3 @4xl/stock-image-filters:flex-row @4xl/stock-image-filters:items-center">
          <SearchInput
            value={searchTerm}
            setValue={setSearchTerm}
            className="min-w-0 flex-1"
            aria-label="Search stock images"
            placeholder="Search title, file name, description, or tag..."
          />

          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant={selectedTagIds.length > 0 ? 'selection' : 'outline'}
                  disabled={isLoadingTags || stockTags.length === 0}
                >
                  <ListFilter className="size-4" />
                  Tags{selectedTagIds.length > 0 ? ` (${selectedTagIds.length})` : ''}
                  <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" size="xl">
                <DropdownMenuLabel>Filter by tag</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {stockTags.length > 0 ? (
                  stockTags.map((tag) => (
                    <DropdownMenuCheckboxItem
                      key={tag.id}
                      checked={selectedTagIdSet.has(tag.id)}
                      onCheckedChange={(checked) => toggleTagFilter(tag.id, checked)}
                    >
                      <span className="truncate">{tag.label}</span>
                    </DropdownMenuCheckboxItem>
                  ))
                ) : (
                  <DropdownMenuCheckboxItem disabled>No tags available</DropdownMenuCheckboxItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant={selectedMerchandisingTagIds.length > 0 ? 'selection' : 'outline'}
                  disabled={isLoadingOccasions || merchandisingTagOptions.length === 0}
                >
                  <CalendarHeart className="size-4" />
                  Occasions
                  {selectedMerchandisingTagIds.length > 0
                    ? ` (${selectedMerchandisingTagIds.length})`
                    : ''}
                  <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" size="xl">
                <DropdownMenuLabel>Filter by occasion</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {merchandisingTagGroups.map((group) => (
                  <div key={group.key}>
                    <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                    {group.tags.map((occasion) => (
                      <DropdownMenuCheckboxItem
                        key={occasion.id}
                        checked={selectedMerchandisingTagIdSet.has(occasion.id)}
                        onCheckedChange={(checked) => toggleOccasionFilter(occasion.id, checked)}
                      >
                        <span className="truncate">{occasion.label}</span>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {hasActiveFilters ? (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
          </div>
        </div>

        {selectedTags.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <Badge key={tag.id} variant="secondary" className="gap-1.5 pr-1">
                <span className="max-w-44 truncate">{tag.label}</span>
                <BadgeAction
                  aria-label={`Remove ${tag.label} filter`}
                  onClick={() => toggleTagFilter(tag.id, false)}
                >
                  <X className="size-3" />
                </BadgeAction>
              </Badge>
            ))}
          </div>
        ) : null}

        {isFetching ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Updating results
          </div>
        ) : null}
      </div>

      {stockImages.length === 0 ? (
        <EmptyState
          className="min-h-80"
          icon={ImageIcon}
          size="spacious"
          surface="card"
          title={hasActiveFilters ? 'No matching stock images' : 'No stock images yet'}
          description={
            hasActiveFilters
              ? 'Try a different search or remove a tag filter.'
              : 'Add global stock images here, then publish them for florist selection.'
          }
          action={
            hasActiveFilters ? (
              <Button type="button" variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : (
              <Can I="create" a="stock-images">
                <CreateActionButton asChild>
                  <Link {...path.stockImages.create().link}>Create Stock Image</Link>
                </CreateActionButton>
              </Can>
            )
          }
        />
      ) : (
        <div className="@container/stock-images space-y-4">
          <div className="columns-1 gap-4 sm:columns-2 @5xl:columns-3 @7xl:columns-4">
            {stockImages.map((asset) => (
              <StockImageCard
                key={asset.id}
                asset={asset}
                showPath={path.stockImages.show(asset.id).link}
              />
            ))}
          </div>
          {hasNextPage ? (
            <div className="grid place-items-center">
              <Button
                type="button"
                variant="outline"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Loading
                  </>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function StockImageCard({ asset, showPath }) {
  const media = getAssetMedia(asset)
  const imageUrl = media.url
  const title = getAssetTitle(asset)
  const tags = asset.tags ?? []

  return (
    <Link
      {...showPath}
      aria-label={`Open ${title}`}
      className="group mb-4 block break-inside-avoid overflow-hidden rounded-xl border border-border bg-card text-left shadow-none transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
    >
      <div className="relative overflow-hidden bg-muted">
        <Badge
          variant={asset.is_published ? 'success' : 'warning'}
          elevation="raised"
          className="absolute left-3 top-3 z-20"
        >
          {asset.is_published ? 'Published' : 'Unpublished'}
        </Badge>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="h-auto w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="grid aspect-square place-items-center text-muted-foreground">
            <ImageIcon className="size-10" />
          </div>
        )}
        <ImageTileMetadata media={media} title={title} visibility="always" />
      </div>
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 p-3">
          {tags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="max-w-full truncate">
              {tag.label}
            </Badge>
          ))}
        </div>
      ) : null}
    </Link>
  )
}
